import { useState, useEffect } from 'react';
import { AlertTriangle, MapPin, Calendar, Building2, Eye, ThumbsUp, RefreshCw, ExternalLink } from 'lucide-react';
import { getEventConflicts } from '../api';
import { getApiErrorMessage } from '../utils/apiError';
import { format, parseISO } from 'date-fns';

function Conflicts() {
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: format(new Date(), 'yyyy-MM-dd'),
    end: format(new Date(Date.now() + 90 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'), // 90 days ahead
  });

  useEffect(() => {
    document.title = 'Conflicts | Dental Events';
    return () => { document.title = 'Dental Events'; };
  }, []);

  useEffect(() => {
    const invalid = dateRange.start && dateRange.end && dateRange.start > dateRange.end;
    if (invalid) {
      setConflicts([]);
      setError(null);
      setLoading(false);
      return;
    }
    loadConflicts();
  }, [dateRange]);

  const loadConflicts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getEventConflicts({
        startDate: dateRange.start,
        endDate: dateRange.end,
      });
      setConflicts(response.data.data.conflicts || []);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load conflicts'));
    } finally {
      setLoading(false);
    }
  };

  const totalConflicts = conflicts.length;
  const totalEvents = conflicts.reduce((sum, c) => sum + c.events.length, 0);
  const dateRangeInvalid = dateRange.start && dateRange.end && dateRange.start > dateRange.end;

  const handleRefresh = () => {
    if (dateRangeInvalid) return;
    loadConflicts();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-2">Schedule Conflicts</h1>
        <p className="text-gray-600">Events happening at the same location on the same date</p>
      </div>

      {/* Date Range Filter */}
      <div className="card">
        {dateRangeInvalid && (
          <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4" role="alert">
            Start date must be before end date.
          </p>
        )}
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[140px]">
            <label htmlFor="conflict-start" className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              id="conflict-start"
              type="date"
              className="input w-full"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label htmlFor="conflict-end" className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              id="conflict-end"
              type="date"
              className="input w-full"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
            />
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading || dateRangeInvalid}
            className="btn-primary flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card bg-red-50 border-red-200">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-xl">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-700">{totalConflicts}</p>
              <p className="text-sm text-red-600">Conflict Groups</p>
            </div>
          </div>
        </div>
        <div className="card bg-amber-50 border-amber-200">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-xl">
              <Calendar className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700">{totalEvents}</p>
              <p className="text-sm text-amber-600">Conflicting Events</p>
            </div>
          </div>
        </div>
        <div className="card bg-dental-50 border-dental-200">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-dental-100 rounded-xl">
              <MapPin className="w-6 h-6 text-dental-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dental-700">
                {new Set(conflicts.map(c => c.location)).size}
              </p>
              <p className="text-sm text-dental-600">Affected Locations</p>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dental-600"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* No Conflicts */}
      {!loading && !error && conflicts.length === 0 && (
        <div className="card text-center py-12">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-lg font-medium mb-2">No Schedule Conflicts</h3>
          <p className="text-gray-500">No events are scheduled at the same location on the same date.</p>
        </div>
      )}

      {/* Conflict Groups */}
      {!loading && !error && conflicts.length > 0 && (
        <div className="space-y-6">
          {conflicts.map((conflict, index) => (
            <div key={index} className="card border-l-4 border-l-red-500">
              {/* Conflict Header */}
              <div className="flex items-start justify-between mb-4 pb-4 border-b">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    <span className="font-semibold text-red-700">
                      {conflict.eventCount} Events Conflicting
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {format(parseISO(conflict.date), 'EEEE, MMMM d, yyyy')}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {conflict.location}
                    </span>
                  </div>
                </div>
              </div>

              {/* Conflicting Events */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {conflict.events.map((event, eventIndex) => (
                  <div
                    key={eventIndex}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <h4 className="font-medium mb-2 line-clamp-2">
                      {event.event_title || 'Untitled Event'}
                    </h4>

                    {event.clinic_name && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                        <Building2 className="w-4 h-4" />
                        <span>{event.clinic_name}</span>
                      </div>
                    )}

                    {event.offer && (
                      <span className="badge badge-success mb-2">{event.offer}</span>
                    )}

                    {/* Metrics */}
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t text-sm">
                      {event.RawAd && (
                        <>
                          {event.RawAd.reach?.label && event.RawAd.reach.label !== '—' && (
                            <div className="flex items-center gap-1 text-gray-500">
                              <Eye className="w-4 h-4" />
                              <span>{event.RawAd.reach.label}</span>
                            </div>
                          )}
                          {event.RawAd.page_like_count && (
                            <div className="flex items-center gap-1 text-gray-500">
                              <ThumbsUp className="w-4 h-4" />
                              <span>{event.RawAd.page_like_count.toLocaleString()}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Treatment Types */}
                    {event.treatment_types && event.treatment_types.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {event.treatment_types.map(t => (
                          <span key={t} className="text-xs bg-dental-100 text-dental-700 px-2 py-0.5 rounded">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* View Ad Button */}
                    {event.adUrl && (
                      <a
                        href={event.adUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-3 text-sm text-dental-600 hover:text-dental-700 font-medium"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View Real Ad
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Conflicts;
